import { useMemo } from 'react';
import Badge from '@mui/material/Badge';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Paper from '@mui/material/Paper';
import AddIcon from '@mui/icons-material/Add';
import { NavLink, useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { useSidebarBadges } from '@/hooks/useSidebarBadges';
import { useTenantScope } from '@/hooks/useTenantScope';
import { activeItemId, bottomNavItems, type SidebarLinkItem } from '@/navigation/sidebarNav';
import { paths, ticketCreateHref } from '@/routes/paths';

const badgeSx = { '& .MuiBadge-badge': { fontSize: '0.625rem', minWidth: 16, height: 16 } } as const;
const CREATE_VALUE = 'nuovo-ticket';

/**
 * Thumb-zone destinations on `xs` only.
 *
 * Hidden from `sm` up (drawer or persistent sidebar takes over) and omitted
 * entirely when the role has no curated list of ≤ 5 primary links.
 */
export function BottomNav(): JSX.Element | null {
  const { pathname } = useLocation();
  const permissions = usePermissions();
  const badges = useSidebarBadges();
  const tenantScope = useTenantScope();
  const items = useMemo(
    () => bottomNavItems(permissions).filter((item) => item.id !== 'ticket-nuovo'),
    [permissions],
  );
  const creating = pathname === paths.ticket.nuovo;
  const currentId = creating ? CREATE_VALUE : activeItemId(pathname, items);
  const newTicketHref = ticketCreateHref({ clienteId: permissions.isStaff ? tenantScope : undefined });

  if (items.length === 0) return null;

  return (
    <Paper
      component="nav"
      aria-label="Navigazione rapida"
      square
      elevation={0}
      sx={{
        display: { xs: 'block', sm: 'none' },
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundImage: 'none',
        pb: 'env(safe-area-inset-bottom, 0px)',
        pl: 'env(safe-area-inset-left, 0px)',
        pr: 'env(safe-area-inset-right, 0px)',
      }}
    >
      <BottomNavigation
        value={currentId ?? false}
        showLabels
        sx={{
          height: (theme) => theme.layout.bottomNavHeight,
          backgroundColor: 'transparent',
        }}
      >
        {withCreateAction(items, permissions.canCreateTickets).map((slot) => {
          if (slot === 'create') {
            return (
              <BottomNavigationAction
                key={CREATE_VALUE}
                component={NavLink}
                to={newTicketHref}
                value={CREATE_VALUE}
                label="Nuovo"
                aria-label="Nuovo ticket"
                icon={
                  <AddIcon
                    sx={{
                      boxSizing: 'border-box',
                      width: 32,
                      height: 32,
                      p: '6px',
                      borderRadius: '50%',
                      bgcolor: 'error.main',
                      color: 'error.contrastText',
                    }}
                  />
                }
                sx={{
                  flex: '0 0 auto',
                  minWidth: 64,
                  maxWidth: 76,
                  px: 0.5,
                  color: 'error.main',
                  '&.Mui-selected': { color: 'error.main' },
                }}
              />
            );
          }

          const Icon = slot.icon;
          const count = slot.badge ? badges[slot.badge] : undefined;
          return (
            <BottomNavigationAction
              key={slot.id}
              component={NavLink}
              to={slot.to}
              end={slot.exact}
              value={slot.id}
              label={slot.label}
              aria-label={slot.label}
              icon={
                Icon ? (
                  <Badge
                    badgeContent={count}
                    color="error"
                    invisible={!count}
                    max={99}
                    sx={badgeSx}
                    componentsProps={{ badge: { 'aria-hidden': true } }}
                  >
                    <Icon />
                  </Badge>
                ) : undefined
              }
            />
          );
        })}
      </BottomNavigation>
    </Paper>
  );
}

/** Plus sits in the middle of the bar so the thumb can reach it. */
function withCreateAction(
  items: readonly SidebarLinkItem[],
  includeCreate: boolean,
): Array<SidebarLinkItem | 'create'> {
  if (!includeCreate) return [...items];
  const at = Math.floor(items.length / 2);
  return [...items.slice(0, at), 'create', ...items.slice(at)];
}
